/**
 * Reads the Stripe data stored in DealPayment.infos.
 *
 * Two shapes live in that blob: a regular payment stores the flat Stripe
 * payload at the root, while an upsell payment nests it under `stripe`.
 * `stripeCheck` is written by StripeReconciliationJob on every verification.
 */
function asRecord(value) {
    return value != null && typeof value === 'object'
        ? value
        : {};
}
function asString(value) {
    return typeof value === 'string' && value !== '' ? value : undefined;
}
export function parsePaymentInfos(infos) {
    if (!infos) {
        return {};
    }
    if (typeof infos === 'object') {
        return infos;
    }
    try {
        const parsed = JSON.parse(infos);
        return asRecord(parsed);
    }
    catch (_a) {
        return {};
    }
}
function readCard(source) {
    const card = asRecord(source.card);
    if (Object.keys(card).length === 0) {
        return undefined;
    }
    return {
        brand: asString(card.brand),
        last4: asString(card.last4),
        expMonth: typeof card.expMonth === 'number' ? card.expMonth : undefined,
        expYear: typeof card.expYear === 'number' ? card.expYear : undefined,
        name: asString(card.name)
    };
}
export function paymentStripeInfo(infos) {
    const root = parsePaymentInfos(infos);
    // An upsell payment keeps its own metadata at the root and nests the Stripe
    // payload; a regular payment has it flat.
    const stripe = root.kind === 'upsell' ? asRecord(root.stripe) : root;
    const check = asRecord(root.stripeCheck);
    return {
        sessionId: asString(root.sessionId),
        paymentIntentId: asString(stripe.id),
        type: asString(stripe.type),
        card: readCard(stripe),
        linkEmail: asString(asRecord(stripe.link).email),
        check: Object.keys(check).length > 0
            ? {
                checkedAt: asString(check.checkedAt),
                sessionStatus: asString(check.sessionStatus),
                paymentStatus: asString(check.paymentStatus),
                paymentIntentStatus: asString(check.paymentIntentStatus),
                error: asString(check.error)
            }
            : undefined
    };
}
export function isPaidOnStripe(check) {
    return ((check === null || check === void 0 ? void 0 : check.paymentStatus) === 'paid' ||
        (check === null || check === void 0 ? void 0 : check.paymentStatus) === 'no_payment_required');
}
export function stripePaymentState(status, info) {
    var _a, _b;
    if (status === 'COMPLETED') {
        return 'COMPLETED';
    }
    if (!info.sessionId) {
        return 'NOT_STARTED';
    }
    // Stripe captured the money but the sale is still pending on our side:
    // either a transient state between two reconciliation ticks, or a
    // completion that keeps failing.
    if (isPaidOnStripe(info.check)) {
        return 'PAID_PENDING';
    }
    if ((_a = info.check) === null || _a === void 0 ? void 0 : _a.error) {
        return 'CHECK_ERROR';
    }
    if (((_b = info.check) === null || _b === void 0 ? void 0 : _b.sessionStatus) === 'expired') {
        return 'SESSION_EXPIRED';
    }
    return 'AWAITING_PAYMENT';
}
/**
 * Coarse "x minutes ago" label — the exact value never matters here, only
 * whether the check is recent.
 */
export function formatCheckedAgo(checkedAt, now = Date.now()) {
    if (!checkedAt) {
        return undefined;
    }
    const timestamp = new Date(checkedAt).getTime();
    if (isNaN(timestamp)) {
        return undefined;
    }
    const minutes = Math.max(0, Math.floor((now - timestamp) / 60000));
    if (minutes < 1) {
        return t('just now');
    }
    if (minutes < 60) {
        return `${minutes} ${t('min ago')}`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return `${hours} ${t('h ago')}`;
    }
    return `${Math.floor(hours / 24)} ${t('d ago')}`;
}
export function formatCardLabel(card) {
    if (!(card === null || card === void 0 ? void 0 : card.last4)) {
        return undefined;
    }
    const brand = card.brand
        ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1)
        : t('Card');
    return `${brand} •••• ${card.last4}`;
}
